import { cloneElement, isValidElement, useId, type ReactNode } from 'react';
import { Tooltip, TruncatedText } from '@forecastify/ui';
import { useT } from '../i18n';

/**
 * A box's title.
 *
 * ⚠️⚠️ **`text-[11px] font-medium tracking-wide uppercase`, MUTED — the mockup's own, and ours was
 * a 13px body-weight heading.** `people.markup.html` uses exactly this on every box it draws:
 * *Állapot*, *Munkarend*, *Részletek*, *Metafieldek*, *Projektek*, *Feladatok*, *Aktivitás*. The
 * difference is not decoration: at 13px in the text colour a box title reads as a line of content
 * and the box looks like it starts one row too early, which is what the screenshot showed.
 */
function BoxTitle({ children }: { readonly children: ReactNode }) {
  return (
    <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
      {children}
    </span>
  );
}

/**
 * The card every section is built from.
 *
 * ⚠️ **It lives at `screens/`, not under `screens/account/`, and the move was the point.** These
 * are layout primitives with no domain in them — C6's account sections were simply the first
 * caller, and C7's delivery dialogs want the identical `Field`. A second copy is how two screens
 * come to disagree about a 12px label, which is exactly the drift `Card` exists to prevent.
 *
 * ⚠️ They are **not** in `@forecastify/ui`, and that is deliberate: `Field` composes a `<label>`
 * and a caller's control with no state and no variants, and a component that adds nothing to the
 * package's API surface but a file is better kept where its callers are. It moves the day a second
 * app needs it.
 *
 * ⚠️ **Measured out of `markup.html`, not chosen:** `rounded-xl border p-4 shadow-sm`, a 13px
 * medium title, a 12px muted hint under it, and the whole column capped at `max-w-[680px]`. One
 * component so those numbers exist once — the mockup repeats them twenty times and a screen that
 * re-typed them would drift on the first card somebody added in a hurry.
 *
 * ⚠️ **Flat, with a hairline border and `shadow-sm`.** The design system spends elevation only on
 * things that actually float; a settings card does not.
 */
export function Card({
  title,
  hint,
  actions,
  children,
}: {
  readonly title: string;
  readonly hint?: string;
  /** Top-right slot — a Save button, a toggle, an "add" control. */
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}) {
  return (
    <section className="bg-card text-card-foreground rounded-xl border p-4 shadow-sm">
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2>
            <BoxTitle>{title}</BoxTitle>
          </h2>
          {hint !== undefined && (
            <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">{hint}</p>
          )}
        </div>
        {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>
      {children !== undefined && <div className="mt-3">{children}</div>}
    </section>
  );
}

/** The column the mockup caps at 680px, with its 12px gap. */
export function CardColumn({ children }: { readonly children: ReactNode }) {
  return <div className="mx-auto flex max-w-[680px] flex-col gap-3">{children}</div>;
}

/**
 * A labelled field.
 *
 * ⚠️ **A real `<label>` wrapping its control**, so the label text is clickable and the accessible
 * name comes from the DOM rather than from an `aria-label` somebody has to remember to write.
 *
 * ⚠️⚠️ **THE MARKER IS ON THE MANDATORY FIELD NOW, NOT ON THE OPTIONAL ONE** — the product owner's
 * ruling of 9 Sep 2026 (`qol-fixes.md` item 8), and it inverts what this component did. Almost
 * every field in a form is optional, so *"— optional"* appeared on almost every field and said
 * nothing; the rare mandatory one said nothing either, which is the half that mattered. A red `*`
 * on the few reverses both.
 *
 * ⚠️ **`hint` is what survives of the old slot, and it is NOT the same thing.** Three call sites
 * were passing real information through it — *open-ended*, *internal*, *from today* — which
 * qualifies the label rather than restating that a field may be left blank. That stays inside the
 * label, for the reason the old note gave: a hint rendered after the control is read after it by
 * most screen readers, which is too late to change what somebody types.
 */
export function Field({
  label,
  required,
  hint,
  note,
  error,
  tourId,
  children,
}: {
  readonly label: string;
  /** A stable hook for the guided tour (`data-tour`) — F-2. */
  readonly tourId?: string;
  /** ⚠️ The form refuses an empty value. Draws a red `*`; absence means nothing is drawn. */
  readonly required?: boolean;
  /** ⚠️ Information that QUALIFIES the label — not a restatement that the field may be blank. */
  readonly hint?: string;
  readonly note?: string;
  readonly error?: string;
  readonly children: ReactNode;
}) {
  const t = useT();
  return (
    <label className="flex flex-col gap-1.5" data-tour={tourId}>
      <span className="text-[12px] font-medium">
        {label}
        {required === true && (
          <>
            {/* ⚠️ The glyph is decorative and the word is what reaches a screen reader — an
                asterisk alone is announced as "star", or as nothing at all. */}
            <span aria-hidden="true" className="text-destructive">
              {' '}
              *
            </span>
            <span className="sr-only"> ({t('common.required')})</span>
          </>
        )}
        {hint !== undefined && <span className="text-muted-foreground font-normal"> · {hint}</span>}
      </span>
      {children}
      {note !== undefined && <span className="text-muted-foreground text-[11.5px]">{note}</span>}
      {/* `state-rules.md`: a field error sits with its field, in the destructive colour. */}
      {error !== undefined && <span className="text-destructive text-[11.5px]">{error}</span>}
    </label>
  );
}

/* ── the detail boxes: a bordered title strip over a list of aligned rows ─────────────────── */

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A property box — `people.markup.html` 333–460.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The title is a BORDERED STRIP, not a heading with padding around it**:
 * `border-b px-3 py-2`, and the rows sit under it in a `p-2` list with a `gap-0.5`. `Card`'s own
 * shape — one padded block with the title inline — is right for a box of prose and wrong for a box
 * of labelled values, which is what every detail rail in this product is.
 */
export function DetailBox({
  title,
  actions,
  tourId,
  children,
}: {
  readonly title: string;
  /** Top-right slot, in the title strip. The mockup's *Módosítás* sits here. */
  readonly actions?: ReactNode;
  /** A stable hook for the guided tour (`data-tour`) — F-2. */
  readonly tourId?: string;
  readonly children: ReactNode;
}) {
  return (
    <section
      className="bg-card text-card-foreground rounded-xl border shadow-sm"
      data-tour={tourId}
    >
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <h2 className="min-w-0 flex-1">
          <BoxTitle>{title}</BoxTitle>
        </h2>
        {actions}
      </div>
      <div className="flex flex-col gap-0.5 p-2">{children}</div>
    </section>
  );
}

/**
 * One labelled value inside a `DetailBox`.
 *
 * ⚠️⚠️ **A TWO-COLUMN GRID, AND THAT IS THE WHOLE POINT OF THE COMPLAINT.** `grid-cols-[92px_1fr]`
 * with `items-center`, so every label in a box starts at the same x and every value does too. Ours
 * stacked the label above the control (`Field`'s shape), which is right for a FORM — where the
 * label belongs to an input somebody is filling in — and wrong for a property rail, where the
 * column of labels is what makes the box scannable.
 *
 * ⚠️ The label is `text-[11.5px]` muted with `pl-1`, matching the mockup exactly; the 1px of
 * padding is what lines it up with the row's own hover surface rather than with the box edge.
 */
export function DetailRow({
  label,
  title,
  children,
}: {
  readonly label: string;
  /** Tooltip — a metafield's help text hangs here in the mockup. */
  readonly title?: string;
  readonly children: ReactNode;
}) {
  /**
   * ⚠️⚠️ **THE LABEL IS JOINED TO THE CONTROL BY ID, AND THE FIRST VERSION LEFT IT A BARE SPAN.**
   * The mockup's row is a `<span>` beside an input, which names nothing — a screen reader reaches
   * the field and announces "edit text", and every row in the box sounds identical. It is a
   * prototype and can afford that; a product cannot, and `design-system/readme.md` requires the
   * name.
   *
   * ⚠️ **`cloneElement` puts the id on the control** rather than threading one through every call
   * site by hand — the version that gets forgotten on the fourth row. `auth-frame.tsx`'s `AuthField`
   * does the same, for the same reason, and records the test that caught it there.
   *
   * ⚠️ **A child that already carries an `id` keeps it.** Some rows hold a whole editor rather than
   * one field, and overwriting its id would break whatever it had already associated.
   */
  const id = useId();
  const labelled =
    isValidElement<{ id?: string }>(children) && children.props.id === undefined
      ? cloneElement(children, { id })
      : children;

  return (
    /**
     * ⚠️⚠️ **`min-h-[30px]` — ONE ROW HEIGHT, OR THE RAIL LOOKS RANDOMLY SPACED.** Reported after
     * testing: *"some have really small space between them, others have more."* The cause is that
     * a row's height was its content's: a plain value is an 18px line, a `Select` or a `TextInput`
     * is 30px, so a rail that alternates between them alternates its row pitch too. Pinning the
     * MINIMUM to the control height makes every row the same, whatever is in it — and it is a
     * minimum rather than a fixed height so a value that wraps still gets its second line.
     */
    <div className="grid min-h-[30px] grid-cols-[92px_minmax(0,1fr)] items-center gap-2">
      <Tooltip asChild content={title}>
        <label className="text-muted-foreground pl-1 text-[11.5px]" htmlFor={id}>
          {label}
        </label>
      </Tooltip>
      {labelled}
    </div>
  );
}

/**
 * The classes that make a control inside a `DetailRow` read as text until it is used.
 *
 * ⚠️⚠️ **THIS IS THE "STRAIGHT INLINE EDITING" THE MOCKUP HAS AND WE DID NOT.** Every value in its
 * details box is a real control wearing no chrome — `border-transparent bg-transparent shadow-none`
 * at `h-[30px]` — so the box reads as a list of facts and becomes editable under the pointer. Ours
 * drew ten bordered input boxes stacked vertically, which is a form, and a form invites filling in
 * rather than reading.
 *
 * ⚠️ **The focus ring is NOT removed.** The control still has to announce itself when it takes
 * focus, or a keyboard user cannot tell where they are — the chrome goes, the affordance does not.
 */
/**
 * ⚠️⚠️ **FOR `TextInput` ONLY NOW — `Select` grew an `inline` prop instead.** Cancelling a
 * component's own classes from the call site works only while both sides stay in the same
 * `tailwind-merge` conflict groups, and it left the chrome-less look depending on a merge nobody
 * can see at the call site: the person rail drew boxed dropdowns while the contract rail did not,
 * from identical props. A component that owns its variants cannot disagree with itself.
 */
export const INLINE_INPUT =
  'h-[30px] w-full border-transparent bg-transparent px-2 text-[12.5px] shadow-none ' +
  /**
   * ⚠️⚠️ **THE BOX APPEARS ON HOVER, AND ITS ABSENCE WAS THE COMPLAINT.** *"It should behave like
   * the text inputs do: it shows nothing until you hover, and the click reveals the box."* Without
   * a hover state a chrome-less control is indistinguishable from a label until you happen to
   * click it — so the rail read as a list of text, and the dropdowns read as a different kind of
   * thing again because only they kept a visible border.
   *
   * ⚠️ **One constant for BOTH `TextInput` and `Select`**, which is what makes them match. They
   * are different elements with different base classes; the only way they look alike in a rail is
   * if the difference is stated once, here.
   */
  'hover:border-input hover:bg-background';

/** Same, for a value rendered as a menu trigger rather than a field. */
export const INLINE_TRIGGER =
  'hover:bg-accent/60 flex h-[30px] w-full items-center gap-2 rounded-md px-2 text-left text-[12.5px] transition-colors';

/** A value nothing can change — the mockup renders these as plain text, not a disabled input. */
export function DetailValue({
  mono,
  children,
}: {
  readonly mono?: boolean;
  readonly children: ReactNode;
}) {
  return (
    /* 22 Oct 2026 · a long unbroken value (an e-mail address) ran past the row — truncated, with
       the whole value in the kit's tooltip. */
    <TruncatedText
      className={mono === true ? 'px-2 text-[12px] tabular-nums' : 'px-2 text-[12.5px]'}
    >
      {children}
    </TruncatedText>
  );
}
